import { ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import {
  CatalogosRegistro,
  PaisCelular,
  RegistroClienteService,
  TipoDocumento,
} from '../services/registro-cliente.service';
import { MarketplaceAuthService } from '../services/marketplace-auth.service';
import { CLAVE_CODIGO_INVITACION } from '../../public-pages/invitacion/invitacion.component';

/** Pasos visibles del wizard. 'login' es una salida lateral para quien ya tiene cuenta. */
type Paso = 'documento' | 'contacto' | 'codigo' | 'clave' | 'listo' | 'login';

/** Version del texto legal aceptado; viaja al backend para dejar constancia (SPEC-030). */
const VERSION_TERMINOS = 'v1';

/** Espera entre reenvios de codigo. El backend admite 3 cada 5 minutos. */
const SEGUNDOS_REENVIO = 60;

/**
 * Registro de cliente en 4 pasos contra api-nest (SPEC-030): documento -> contacto ->
 * codigo por WhatsApp -> contrasena. Pagina propia (no modal) porque un wizard de 4 pasos
 * con teclado numerico y contadores no entra comodo en el drawer de auth-modal.
 *
 * Formularios REACTIVOS y no template-driven como el resto del marketplace: casi toda la
 * validacion de este flujo es cruzada o dinamica (email = confirmacion, clave =
 * confirmacion, largo del documento segun el catalogo, largo del celular segun el pais).
 * Con ngModel eso termina en un puñado de getters que recalculan a mano en cada ciclo de
 * deteccion; con FormGroup son validadores declarativos que ya devuelven el estado.
 *
 * OJO con el estado: todo vive local al componente. NgRx en este proyecto solo maneja el
 * spinner global y el customer del JWT, no formularios.
 */
@Component({
  selector: 'app-registro-cliente',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink],
  styles: [`
    :host{ display:block; background:#f6f8fb; min-height:100vh; }
    .top{ background:linear-gradient(135deg,#00A7DB,#0f6ea8); color:#fff; padding:22px 20px 46px; }
    .wrap{ max-width:620px; margin:0 auto; padding:0 18px; }
    .back{ color:#fff; opacity:.9; text-decoration:none; font-size:14px; display:inline-block; margin-bottom:12px; }
    .top h1{ margin:0; font-size:1.45rem; font-weight:800; }
    .top .sub{ opacity:.92; font-size:14px; margin-top:4px; }

    .steps{ display:flex; gap:8px; margin-top:18px; }
    .steps .s{ flex:1; }
    .steps .bar{ height:4px; border-radius:3px; background:rgba(255,255,255,.28); }
    .steps .s.done .bar, .steps .s.now .bar{ background:#fff; }
    .steps .lbl{ font-size:11px; margin-top:6px; opacity:.7; font-weight:700; letter-spacing:.3px; text-transform:uppercase; }
    .steps .s.now .lbl, .steps .s.done .lbl{ opacity:1; }
    .steps .s.skip .lbl{ text-decoration:line-through; opacity:.5; }

    .content{ max-width:620px; margin:-28px auto 60px; padding:0 18px; }
    .card{ background:#fff; border:1px solid #eef2f7; border-radius:14px; box-shadow:0 6px 18px rgba(9,50,80,.07); padding:22px; }
    h2{ margin:0 0 4px; font-size:1.15rem; color:#12263a; }
    .hint{ font-size:13.5px; color:#5b6b7b; margin:0 0 14px; line-height:1.45; }

    label{ display:block; font-size:12.5px; font-weight:700; color:#5b6b7b; margin:12px 0 5px; }
    input, select{ width:100%; padding:12px; border:1px solid #d6dfe8; border-radius:9px; font-size:14px; box-sizing:border-box; background:#fff; color:#12263a; }
    input:focus, select:focus{ outline:0; border-color:#00A7DB; box-shadow:0 0 0 3px rgba(0,167,219,.15); }
    input.bad, select.bad{ border-color:#e2a09a; }
    .two{ display:flex; gap:12px; }
    .two > *{ flex:1; min-width:0; }
    .tel{ display:flex; gap:8px; }
    .tel select{ flex:0 0 128px; }
    .tel input{ flex:1; }

    /* Se llamaba .check y colisionaba con una regla del styles.scss global:
       .check{ position:absolute; width:24px; height:24px; top:7%; right:5% }
       No competian por las mismas propiedades —esta define display/gap/tipografia y
       aquella position/tamano— asi que no habia una que ganara: se SUMABAN, y el label
       terminaba flotando fuera de la tarjeta, arriba a la derecha, encajonado en 24px.
       Por eso el texto se veia partido en una columna finita. Renombrada a .terminos,
       que no la usa nadie mas. */
    .terminos{ display:flex; align-items:flex-start; gap:9px; margin-top:16px; font-size:13px; color:#48586a; line-height:1.4; }
    .terminos input{ width:auto; margin-top:2px; flex:0 0 auto; }

    .btn{ background:#00A7DB; color:#fff; border:0; border-radius:10px; padding:13px; font-weight:700; cursor:pointer; width:100%; margin-top:18px; font-size:15px; }
    .btn:disabled{ opacity:.6; cursor:default; }
    .btn.ghost{ background:#fff; color:#0f6ea8; border:1px solid #cfe3ef; margin-top:10px; }
    .link{ background:none; border:0; color:#0f6ea8; font-weight:700; cursor:pointer; padding:0; font-size:13px; }
    .link:disabled{ color:#8a97a8; cursor:default; font-weight:600; }

    .err{ color:#c0392b; font-size:13px; margin-top:12px; }
    .fielderr{ color:#c0392b; font-size:12px; margin-top:5px; }
    .warn{ background:#fff8e6; border:1px solid #f2e2b4; color:#7a5c14; font-size:13px; border-radius:9px; padding:10px 12px; margin-top:14px; line-height:1.45; }
    .ok{ background:#eafaf1; border:1px solid #bfe6d0; color:#1a7f47; font-size:13px; border-radius:9px; padding:10px 12px; margin-top:14px; line-height:1.45; }
    .masked{ background:#f7fafc; border-left:3px solid #00A7DB; border-radius:0 8px 8px 0; padding:10px 12px; font-size:13.5px; color:#33475b; margin-bottom:6px; }
    .masked b{ font-size:15px; letter-spacing:1px; }
    .loading{ text-align:center; color:#8a97a8; padding:30px 10px; font-size:14px; }
    .foot{ text-align:center; margin-top:16px; font-size:13px; color:#7a8798; }

    .codigo{ letter-spacing:.5em; text-align:center; font-size:22px; font-weight:700; }
    .done{ text-align:center; padding:14px 0 4px; }
    .done .tick{ width:62px; height:62px; border-radius:50%; background:#eafaf1; color:#1a7f47; display:grid; place-items:center; font-size:30px; margin:0 auto 14px; }

    @media (max-width:560px){
      .two{ flex-direction:column; gap:0; }
      .tel select{ flex:0 0 108px; }
    }
  `],
  template: `
  <div class="top">
    <div class="wrap">
      <a class="back" routerLink="/marketplace">← Volver</a>
      <h1>{{ paso === 'login' ? 'Iniciar sesión' : 'Crear tu cuenta' }}</h1>
      <div class="sub">
        {{ paso === 'login'
            ? 'Ingresá con tu documento y tu contraseña.'
            : 'Con tu documento accedés a tus citas, tus puntos y tus reservas.' }}
      </div>

      @if (paso !== 'login') {
        <div class="steps">
          @for (s of etiquetasPasos; track s.clave) {
            <div class="s" [class.now]="s.clave === paso" [class.done]="estaHecho(s.clave)" [class.skip]="esSalteado(s.clave)">
              <div class="bar"></div>
              <div class="lbl">{{ s.texto }}</div>
            </div>
          }
        </div>
      }
    </div>
  </div>

  <div class="content">
    <div class="card">

      @if (cargandoCatalogos) {
        <div class="loading">Cargando…</div>
      } @else if (errorCatalogos) {
        <div class="err">{{ errorCatalogos }}</div>
        <button class="btn" type="button" (click)="cargarCatalogos()">Reintentar</button>
      } @else {

        @switch (paso) {

          <!-- ------------------------------------------------ paso 1: documento -->
          @case ('documento') {
            <h2>Tu documento</h2>
            <p class="hint">Con esto vemos si ya estás en el sistema de la clínica.</p>

            <form [formGroup]="formDocumento" (ngSubmit)="enviarDocumento()">
              <label for="tipoDoc">Tipo de documento</label>
              <select id="tipoDoc" formControlName="idTipoDocumento">
                @for (t of tiposDocumento; track t.id) {
                  <option [value]="t.id">{{ t.descripcion }}</option>
                }
              </select>

              <label for="numDoc">Número de documento</label>
              <input id="numDoc" formControlName="numeroDocumento" autocomplete="off"
                     [class.bad]="invalido(formDocumento, 'numeroDocumento')"
                     [placeholder]="placeholderDocumento" />
              @if (invalido(formDocumento, 'numeroDocumento')) {
                <div class="fielderr">{{ errorDocumento }}</div>
              }

              <label class="terminos">
                <input type="checkbox" formControlName="aceptaTerminos" />
                <span>Acepto los términos y condiciones y el tratamiento de mis datos personales.</span>
              </label>

              <button class="btn" type="submit" [disabled]="cargando">
                {{ cargando ? 'Verificando…' : 'Continuar' }}
              </button>
            </form>

            @if (error) {
              <div class="err">{{ error }}</div>
              @if (yaTieneCuenta) {
                <button class="btn ghost" type="button" (click)="irALogin()">Ya tengo cuenta, iniciar sesión</button>
              }
            }

            <div class="foot">
              ¿Ya tenés cuenta? <button class="link" type="button" (click)="irALogin()">Iniciá sesión</button>
            </div>
          }

          <!-- ------------------------------------------------ paso 2: contacto -->
          @case ('contacto') {
            <h2>Tus datos</h2>
            <p class="hint">Al celular que cargues te enviamos el código de verificación por WhatsApp.</p>

            <form [formGroup]="formContacto" (ngSubmit)="enviarContacto()">
              <div class="two">
                <div>
                  <label for="nombres">Nombres</label>
                  <input id="nombres" formControlName="nombres" [class.bad]="invalido(formContacto, 'nombres')" />
                </div>
                <div>
                  <label for="apellidos">Apellidos</label>
                  <input id="apellidos" formControlName="apellidos" />
                </div>
              </div>

              <label for="email">Correo</label>
              <input id="email" type="email" formControlName="email" autocomplete="email"
                     [class.bad]="invalido(formContacto, 'email')" placeholder="tu@email.com" />
              @if (invalido(formContacto, 'email')) {
                <div class="fielderr">Ingresá un correo válido.</div>
              }

              <label for="email2">Repetí el correo</label>
              <input id="email2" type="email" formControlName="emailConfirmacion" autocomplete="off"
                     [class.bad]="emailsDistintos" placeholder="tu@email.com" />
              @if (emailsDistintos) {
                <div class="fielderr">Los correos no coinciden.</div>
              }

              <label for="celular">Celular</label>
              <div class="tel">
                <select id="paisCelular" formControlName="paisCelular" aria-label="Código de país">
                  @for (p of paisesCelular; track p.codigo) {
                    <option [value]="p.codigo">{{ p.iso }} +{{ p.codigo }}</option>
                  }
                </select>
                <input id="celular" formControlName="celular" inputmode="numeric" autocomplete="tel"
                       [class.bad]="invalido(formContacto, 'celular')"
                       [placeholder]="placeholderCelular" (input)="soloDigitos(formContacto, 'celular')" />
              </div>
              @if (invalido(formContacto, 'celular')) {
                <div class="fielderr">{{ errorCelular }}</div>
              }

              <label for="codigoReferido">¿Te invitó alguien? Código (opcional)</label>
              <input id="codigoReferido" formControlName="codigoReferido" autocomplete="off" maxlength="12"
                     [class.bad]="invalido(formContacto, 'codigoReferido')" placeholder="Ej.: K7M2QX" />
              @if (invalido(formContacto, 'codigoReferido')) {
                <div class="fielderr">Solo letras y números.</div>
              }

              <button class="btn" type="submit" [disabled]="cargando">
                {{ cargando ? 'Creando tu cuenta…' : 'Continuar' }}
              </button>
            </form>

            @if (error) { <div class="err">{{ error }}</div> }

            <div class="foot">
              <button class="link" type="button" (click)="volverADocumento()">← Cambiar el documento</button>
            </div>
          }

          <!-- ------------------------------------------------ paso 3: codigo -->
          @case ('codigo') {
            <h2>Código de verificación</h2>

            @if (modo === 'reclamo') {
              <!-- En reclamo el celular NO se elige: el codigo va al que ya figura en la
                   ficha clinica. Si el usuario pudiera poner otro, cualquiera reclamaria
                   la ficha de un tercero tipeando su documento. Por eso solo se muestra
                   enmascarado, para que reconozca el suyo sin exponerlo. -->
              <p class="hint">
                Tu documento ya figura en la clínica. Te enviamos el código al celular registrado en tu ficha:
              </p>
              <div class="masked">WhatsApp a <b>{{ celularEnmascarado }}</b></div>
              <p class="hint">Si ese número ya no es tuyo, acercate a recepción para actualizarlo.</p>
            } @else {
              <p class="hint">Te enviamos un código de 6 dígitos por WhatsApp. Vence en {{ expiraEnMinutos }} minutos.</p>
            }

            @if (avisoDev) { <div class="warn">{{ avisoDev }}</div> }

            <form [formGroup]="formCodigo" (ngSubmit)="verificarCodigo()">
              <label for="codigo">Código</label>
              <input id="codigo" class="codigo" formControlName="codigo" inputmode="numeric" maxlength="6"
                     autocomplete="one-time-code" placeholder="······"
                     [class.bad]="invalido(formCodigo, 'codigo')" (input)="soloDigitos(formCodigo, 'codigo')" />
              @if (invalido(formCodigo, 'codigo')) {
                <div class="fielderr">El código tiene 6 dígitos.</div>
              }

              <button class="btn" type="submit" [disabled]="cargando">
                {{ cargando ? 'Verificando…' : 'Verificar' }}
              </button>
            </form>

            @if (error) { <div class="err">{{ error }}</div> }

            <div class="foot">
              @if (segundosReenvio > 0) {
                <button class="link" type="button" disabled>Reenviar código en {{ segundosReenvio }}s</button>
              } @else {
                <button class="link" type="button" [disabled]="reenviando" (click)="pedirCodigo(true)">
                  {{ reenviando ? 'Enviando…' : 'Reenviar código' }}
                </button>
              }
            </div>
          }

          <!-- ------------------------------------------------ paso 4: clave -->
          @case ('clave') {
            <h2>Tu contraseña</h2>
            <p class="hint">Último paso. Mínimo 8 caracteres.</p>

            <form [formGroup]="formClave" (ngSubmit)="definirClave()">
              <label for="clave">Contraseña</label>
              <input id="clave" type="password" formControlName="clave" autocomplete="new-password"
                     [class.bad]="invalido(formClave, 'clave')" />
              @if (invalido(formClave, 'clave')) {
                <div class="fielderr">La contraseña debe tener al menos 8 caracteres.</div>
              }

              <label for="clave2">Repetí la contraseña</label>
              <input id="clave2" type="password" formControlName="claveConfirmacion" autocomplete="new-password"
                     [class.bad]="clavesDistintas" />
              @if (clavesDistintas) {
                <div class="fielderr">Las contraseñas no coinciden.</div>
              }

              <button class="btn" type="submit" [disabled]="cargando">
                {{ cargando ? 'Guardando…' : 'Crear mi cuenta' }}
              </button>
            </form>

            @if (error) { <div class="err">{{ error }}</div> }
          }

          <!-- ------------------------------------------------ fin -->
          @case ('listo') {
            <div class="done">
              <div class="tick">✓</div>
              <h2>¡Listo{{ nombreCliente ? ', ' + nombreCliente : '' }}!</h2>
              <p class="hint">Tu cuenta quedó creada.</p>
            </div>

            @if (sesionIniciada) {
              <div class="ok">Ya estás dentro. Podés ver tus citas y tus puntos en Mi cuenta.</div>
              <button class="btn" type="button" (click)="irAMiCuenta()">Ir a Mi cuenta</button>
            } @else {
              <div class="warn">
                Creamos tu cuenta, pero no pudimos iniciar la sesión automáticamente.
                Ingresá con tu documento y la contraseña que acabás de elegir.
              </div>
              <button class="btn" type="button" (click)="irALogin()">Iniciar sesión</button>
            }
          }

          <!-- ------------------------------------------------ login lateral -->
          @case ('login') {
            <h2>Ingresá</h2>
            <p class="hint">Se ingresa con el documento, no con el correo: buena parte de los clientes de la clínica no tiene correo cargado.</p>

            <form [formGroup]="formLogin" (ngSubmit)="iniciarSesion()">
              <label for="tipoDocL">Tipo de documento</label>
              <select id="tipoDocL" formControlName="idTipoDocumento">
                @for (t of tiposDocumento; track t.id) {
                  <option [value]="t.id">{{ t.descripcion }}</option>
                }
              </select>

              <label for="numDocL">Número de documento</label>
              <input id="numDocL" formControlName="numeroDocumento" autocomplete="username" />

              <label for="claveL">Contraseña</label>
              <input id="claveL" type="password" formControlName="clave" autocomplete="current-password" />

              <button class="btn" type="submit" [disabled]="cargando || formLogin.invalid">
                {{ cargando ? 'Ingresando…' : 'Ingresar' }}
              </button>
            </form>

            @if (error) { <div class="err">{{ error }}</div> }

            <div class="foot">
              ¿No tenés cuenta? <button class="link" type="button" (click)="reiniciar()">Creá una</button>
            </div>
          }
        }
      }
    </div>
  </div>
  `,
})
export class RegistroClienteComponent implements OnInit, OnDestroy {
  // ---------------------------------------------------------------- estado
  paso: Paso = 'documento';
  modo: 'nuevo' | 'reclamo' | null = null;

  private readonly ruta = inject(ActivatedRoute);

  cargando = false;
  cargandoCatalogos = true;
  errorCatalogos = '';
  error = '';

  /** El 409 del paso 1 tiene una accion concreta: mandar al login. Los otros errores no. */
  yaTieneCuenta = false;

  tiposDocumento: TipoDocumento[] = [];
  paisesCelular: PaisCelular[] = [];

  celularEnmascarado = '';
  expiraEnMinutos = 10;
  avisoDev = '';
  nombreCliente = '';
  sesionIniciada = false;

  reenviando = false;
  segundosReenvio = 0;
  private timerReenvio: ReturnType<typeof setInterval> | null = null;

  // ---------------------------------------------------------------- forms
  formDocumento: FormGroup;
  formContacto: FormGroup;
  formCodigo: FormGroup;
  formClave: FormGroup;
  formLogin: FormGroup;

  constructor(
    private fb: FormBuilder,
    private registro: RegistroClienteService,
    private mktAuth: MarketplaceAuthService,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {
    this.formDocumento = this.fb.group({
      idTipoDocumento: [null as number | null, Validators.required],
      numeroDocumento: ['', Validators.required],
      aceptaTerminos: [false, Validators.requiredTrue],
    });

    this.formContacto = this.fb.group(
      {
        nombres: ['', [Validators.required, Validators.minLength(2)]],
        apellidos: [''],
        email: ['', [Validators.required, Validators.pattern(/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/)]],
        emailConfirmacion: ['', Validators.required],
        paisCelular: [null as number | null, Validators.required],
        celular: ['', Validators.required],
        codigoReferido: ['', Validators.pattern(/^[A-Za-z0-9]{0,12}$/)],
      },
      { validators: this.camposIguales('email', 'emailConfirmacion', 'emailsDistintos') },
    );

    this.formCodigo = this.fb.group({
      codigo: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
    });

    this.formClave = this.fb.group(
      {
        clave: ['', [Validators.required, Validators.minLength(8)]],
        claveConfirmacion: ['', Validators.required],
      },
      { validators: this.camposIguales('clave', 'claveConfirmacion', 'clavesDistintas') },
    );

    this.formLogin = this.fb.group({
      idTipoDocumento: [null as number | null, Validators.required],
      numeroDocumento: ['', Validators.required],
      clave: ['', Validators.required],
    });

    // El largo exacto lo manda el catalogo, no una constante: "DNI = 8" hardcodeado en el
    // front se desincroniza el dia que la tabla cambie o entre otro tipo de documento.
    this.formDocumento.get('idTipoDocumento')!.valueChanges.subscribe(() => this.aplicarReglasDocumento());
    this.formContacto.get('paisCelular')!.valueChanges.subscribe(() => this.aplicarReglasCelular());
  }

  ngOnInit(): void {
    this.registro.reset();
    this.cargarCatalogos();
    this.precargarCodigoInvitacion();
  }

  /** Del enlace /r/<codigo>: viene en la URL o quedó guardado en el navegador. */
  private precargarCodigoInvitacion(): void {
    let codigo = this.ruta.snapshot.queryParamMap.get('codigo') ?? '';
    if (!codigo) {
      try {
        codigo = localStorage.getItem(CLAVE_CODIGO_INVITACION) ?? '';
      } catch {
        codigo = '';
      }
    }
    codigo = codigo.trim().toUpperCase();
    if (/^[A-Z0-9]{4,12}$/.test(codigo)) this.formContacto.patchValue({ codigoReferido: codigo });
  }

  ngOnDestroy(): void {
    this.detenerContador();
    // El token de registro no debe sobrevivir a la pagina que lo usa.
    this.registro.reset();
  }

  // ---------------------------------------------------------------- catalogos
  cargarCatalogos(): void {
    this.cargandoCatalogos = true;
    this.errorCatalogos = '';
    this.registro.catalogos().subscribe({
      next: (c: CatalogosRegistro) => {
        this.tiposDocumento = c?.tiposDocumento ?? [];
        this.paisesCelular = c?.paisesCelular ?? [];
        // Preselección: el primer tipo (DNI) y el primer país (Perú, el 99% de la base).
        if (this.tiposDocumento.length) {
          this.formDocumento.patchValue({ idTipoDocumento: this.tiposDocumento[0].id });
          this.formLogin.patchValue({ idTipoDocumento: this.tiposDocumento[0].id });
        }
        if (this.paisesCelular.length) {
          this.formContacto.patchValue({ paisCelular: this.paisesCelular[0].codigo });
        }
        this.cargandoCatalogos = false;
        this.cdr.detectChanges();
      },
      error: (e) => {
        this.errorCatalogos = this.mensaje(e, 'No pudimos cargar el formulario. Revisá tu conexión.');
        this.cargandoCatalogos = false;
        this.cdr.detectChanges();
      },
    });
  }

  // ---------------------------------------------------------------- paso 1
  enviarDocumento(): void {
    this.error = '';
    this.yaTieneCuenta = false;
    this.formDocumento.markAllAsTouched();
    if (this.formDocumento.invalid || this.cargando) return;

    this.cargando = true;
    const v = this.formDocumento.value;
    this.registro
      .validarDocumento({
        idTipoDocumento: Number(v.idTipoDocumento),
        numeroDocumento: (v.numeroDocumento ?? '').trim(),
        aceptaTerminos: !!v.aceptaTerminos,
        versionTerminos: VERSION_TERMINOS,
      })
      .subscribe({
        next: (r) => {
          this.cargando = false;
          this.modo = r.modo;

          if (r.modo === 'reclamo') {
            // El paciente ya existe en la ficha clinica: el paso 2 crearia un duplicado.
            // El token ya vino en esta misma respuesta, asi que vamos derecho al codigo.
            this.celularEnmascarado = r.celularEnmascarado;
            this.paso = 'codigo';
            this.pedirCodigo(false);
          } else {
            this.paso = 'contacto';
          }
          this.cdr.detectChanges();
        },
        error: (e: HttpErrorResponse) => {
          this.cargando = false;
          // 409 = ya tiene cuenta, o figura sin celular. En ambos casos el mensaje del
          // backend es la accion a seguir, por eso se muestra tal cual.
          this.yaTieneCuenta = e?.status === 409;
          this.error = this.mensaje(e, 'No pudimos validar tu documento.');
          this.cdr.detectChanges();
        },
      });
  }

  volverADocumento(): void {
    this.error = '';
    this.modo = null;
    this.registro.reset();
    this.paso = 'documento';
  }

  // ---------------------------------------------------------------- paso 2
  enviarContacto(): void {
    this.error = '';
    this.formContacto.markAllAsTouched();
    if (this.formContacto.invalid || this.cargando) return;

    this.cargando = true;
    const d = this.formDocumento.value;
    const c = this.formContacto.value;
    this.registro
      .registrarContacto({
        idTipoDocumento: Number(d.idTipoDocumento),
        numeroDocumento: (d.numeroDocumento ?? '').trim(),
        aceptaTerminos: !!d.aceptaTerminos,
        versionTerminos: VERSION_TERMINOS,
        nombres: (c.nombres ?? '').trim(),
        apellidos: (c.apellidos ?? '').trim(),
        email: (c.email ?? '').trim(),
        emailConfirmacion: (c.emailConfirmacion ?? '').trim(),
        // El backend espera un entero; el <select> devuelve string.
        paisCelular: Number(c.paisCelular),
        ...((c.codigoReferido ?? '').trim()
          ? { codigoReferido: (c.codigoReferido ?? '').trim().toUpperCase() }
          : {}),
        celular: (c.celular ?? '').replace(/\D/g, ''),
      })
      .subscribe({
        next: () => {
          try {
            localStorage.removeItem(CLAVE_CODIGO_INVITACION);
          } catch {
            // Sin almacenamiento no hay nada que limpiar.
          }
          this.cargando = false;
          this.paso = 'codigo';
          this.pedirCodigo(false);
          this.cdr.detectChanges();
        },
        error: (e: HttpErrorResponse) => {
          this.cargando = false;
          this.error = this.mensaje(e, 'No pudimos crear tu cuenta.');
          this.cdr.detectChanges();
        },
      });
  }

  // ---------------------------------------------------------------- paso 3
  /** `manual` distingue el reenvio pedido por el usuario del envio automatico al entrar. */
  pedirCodigo(manual: boolean): void {
    if (this.reenviando || this.segundosReenvio > 0) return;
    this.error = '';
    this.avisoDev = '';
    this.reenviando = true;

    this.registro.enviarCodigo().subscribe({
      next: (r) => {
        this.reenviando = false;
        this.expiraEnMinutos = r?.expiraEnMinutos ?? 10;
        // En dev el gateway de WhatsApp no existe y el backend responde 200 con
        // canal 'log-dev': el codigo sale por el log del servidor. Es exito, no error.
        if (r && r.enviado === false) {
          this.avisoDev =
            'Entorno de desarrollo: el código no se envía por WhatsApp, sale en el log del servidor de api-nest.';
        }
        this.iniciarContador();
        this.cdr.detectChanges();
      },
      error: (e: HttpErrorResponse) => {
        this.reenviando = false;
        if (e?.status === 429) {
          // Unico caso donde NO se muestra el message del backend: el 429 lo produce el
          // ThrottlerGuard de Nest, cuyo texto es el generico en ingles ("Too many
          // requests"), no uno redactado para el usuario. El limite es 3 cada 5 minutos.
          this.error = 'Pediste demasiados códigos. Esperá unos minutos antes de intentar de nuevo.';
        } else if (e?.status === 503) {
          this.error = this.mensaje(e, 'WhatsApp no está disponible en este momento. Probá en unos minutos.');
        } else if (e?.status === 401) {
          this.error = 'Tu sesión de registro venció. Empezá de nuevo.';
        } else {
          this.error = this.mensaje(e, 'No pudimos enviar el código.');
        }
        // El primer envio (automatico) que falla igual deja pedir el reenvio a mano.
        if (manual) this.iniciarContador();
        this.cdr.detectChanges();
      },
    });
  }

  verificarCodigo(): void {
    this.error = '';
    this.formCodigo.markAllAsTouched();
    if (this.formCodigo.invalid || this.cargando) return;

    this.cargando = true;
    this.registro.verificarCodigo((this.formCodigo.value.codigo ?? '').trim()).subscribe({
      // El servicio ya guardo el token NUEVO que devuelve este endpoint: es el mismo
      // cliente pero con verificado=true, y el paso de la clave rechaza (401) al anterior.
      next: () => {
        this.cargando = false;
        this.detenerContador();
        this.paso = 'clave';
        this.cdr.detectChanges();
      },
      error: (e: HttpErrorResponse) => {
        this.cargando = false;
        this.error = this.mensaje(e, 'El código no es válido o venció.');
        this.cdr.detectChanges();
      },
    });
  }

  // ---------------------------------------------------------------- paso 4
  definirClave(): void {
    this.error = '';
    this.formClave.markAllAsTouched();
    if (this.formClave.invalid || this.cargando) return;

    this.cargando = true;
    const { clave, claveConfirmacion } = this.formClave.value;
    this.registro.definirClave(clave, claveConfirmacion).subscribe({
      // Este paso ya devuelve la sesion (access_token + nombres): antes habia que
      // encadenar un /login con las credenciales recien creadas, que era un viaje de mas
      // y una ventana donde el registro salia bien pero el usuario quedaba afuera.
      next: (r) => {
        if (r?.access_token) {
          this.guardarSesion(r.access_token, r.idCliente, r.nombres);
          this.nombreCliente = r.nombres ?? '';
          this.sesionIniciada = true;
        } else {
          // Backend viejo, sin sesion en la respuesta: la cuenta igual quedo creada.
          this.sesionIniciada = false;
        }
        this.finalizar();
      },
      error: (e: HttpErrorResponse) => {
        this.cargando = false;
        this.error = this.mensaje(e, 'No pudimos guardar tu contraseña.');
        this.cdr.detectChanges();
      },
    });
  }

  private finalizar(): void {
    this.cargando = false;
    this.registro.reset(); // el token de registro ya no sirve para nada
    this.paso = 'listo';
    this.cdr.detectChanges();
  }

  // ---------------------------------------------------------------- login
  irALogin(): void {
    this.error = '';
    // Arrastra lo que ya tipeó en el paso 1 para no hacérselo escribir dos veces.
    const d = this.formDocumento.value;
    this.formLogin.patchValue({
      idTipoDocumento: d.idTipoDocumento ?? this.tiposDocumento[0]?.id ?? null,
      numeroDocumento: d.numeroDocumento ?? '',
    });
    this.paso = 'login';
  }

  iniciarSesion(): void {
    this.error = '';
    this.formLogin.markAllAsTouched();
    if (this.formLogin.invalid || this.cargando) return;

    this.cargando = true;
    const v = this.formLogin.value;
    this.registro.login(Number(v.idTipoDocumento), (v.numeroDocumento ?? '').trim(), v.clave).subscribe({
      next: (r) => {
        this.guardarSesion(r.access_token, r.idCliente, r.nombres);
        this.cargando = false;
        this.irAMiCuenta();
      },
      error: (e: HttpErrorResponse) => {
        this.cargando = false;
        this.error = this.mensaje(e, 'Documento o contraseña incorrectos.');
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Deja la sesion donde el resto del marketplace la busca (mkt_access / mkt_user), que es
   * de donde la lee marketplaceAuthInterceptor para firmar las llamadas a /marketplace/*.
   */
  private guardarSesion(accessToken: string, idCliente: number, nombres: string): void {
    this.mktAuth.store({
      accessToken,
      user: {
        sub: idCliente,
        tipo: 'CLIENTE',
        idEmpresa: null,
        email: (this.formContacto.value.email ?? '').trim(),
        nombre: nombres ?? null,
      },
    });
  }

  irAMiCuenta(): void {
    this.router.navigate(['/marketplace/mi-cuenta']);
  }

  reiniciar(): void {
    this.registro.reset();
    this.error = '';
    this.avisoDev = '';
    this.modo = null;
    this.sesionIniciada = false;
    this.formContacto.reset({ paisCelular: this.paisesCelular[0]?.codigo ?? null });
    this.formCodigo.reset({ codigo: '' });
    this.formClave.reset({ clave: '', claveConfirmacion: '' });
    this.detenerContador();
    this.paso = 'documento';
  }

  // ---------------------------------------------------------------- contador
  private iniciarContador(): void {
    this.detenerContador();
    this.segundosReenvio = SEGUNDOS_REENVIO;
    this.timerReenvio = setInterval(() => {
      this.segundosReenvio--;
      if (this.segundosReenvio <= 0) this.detenerContador();
      this.cdr.detectChanges();
    }, 1000);
  }

  private detenerContador(): void {
    if (this.timerReenvio) {
      clearInterval(this.timerReenvio);
      this.timerReenvio = null;
    }
    this.segundosReenvio = 0;
  }

  // ---------------------------------------------------------------- validacion
  /** Reglas del documento segun el catalogo: `longitud` es el largo EXACTO (null = libre). */
  private aplicarReglasDocumento(): void {
    const ctrl = this.formDocumento.get('numeroDocumento')!;
    const largo = this.tipoDocumentoActual?.longitud ?? null;
    const reglas: ValidatorFn[] = [Validators.required, Validators.pattern(/^[A-Za-z0-9-]{6,20}$/)];
    if (largo) reglas.push(Validators.minLength(largo), Validators.maxLength(largo));
    ctrl.setValidators(reglas);
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  /** El largo del celular tambien viene del catalogo: no es 9 para todos los paises. */
  private aplicarReglasCelular(): void {
    const ctrl = this.formContacto.get('celular')!;
    const largo = this.paisActual?.longitud ?? null;
    const reglas: ValidatorFn[] = [Validators.required, Validators.pattern(/^\d+$/)];
    if (largo) reglas.push(Validators.minLength(largo), Validators.maxLength(largo));
    ctrl.setValidators(reglas);
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  /** Validador cruzado a nivel grupo, al estilo de `passwordsIguales` del registro legacy. */
  private camposIguales(a: string, b: string, clave: string): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null =>
      group.get(a)?.value === group.get(b)?.value ? null : { [clave]: true };
  }

  invalido(form: FormGroup, campo: string): boolean {
    const c = form.get(campo);
    return !!c && c.invalid && (c.touched || c.dirty);
  }

  get emailsDistintos(): boolean {
    const c = this.formContacto.get('emailConfirmacion');
    return !!this.formContacto.errors?.['emailsDistintos'] && !!c && (c.touched || c.dirty);
  }

  get clavesDistintas(): boolean {
    const c = this.formClave.get('claveConfirmacion');
    return !!this.formClave.errors?.['clavesDistintas'] && !!c && (c.touched || c.dirty);
  }

  /** El backend guarda solo digitos; limpiar en el input evita un 400 por un espacio. */
  soloDigitos(form: FormGroup, campo: string): void {
    const c = form.get(campo)!;
    const limpio = (c.value ?? '').replace(/\D/g, '');
    if (limpio !== c.value) c.setValue(limpio, { emitEvent: false });
  }

  // ---------------------------------------------------------------- helpers de vista
  get tipoDocumentoActual(): TipoDocumento | undefined {
    const id = Number(this.formDocumento.value.idTipoDocumento);
    return this.tiposDocumento.find((t) => t.id === id);
  }

  get paisActual(): PaisCelular | undefined {
    const cod = Number(this.formContacto.value.paisCelular);
    return this.paisesCelular.find((p) => p.codigo === cod);
  }

  get placeholderDocumento(): string {
    const t = this.tipoDocumentoActual;
    return t?.longitud ? `${t.longitud} caracteres` : 'Tu número de documento';
  }

  get errorDocumento(): string {
    const t = this.tipoDocumentoActual;
    if (t?.longitud) return `El ${t.descripcion} debe tener ${t.longitud} caracteres.`;
    return 'Ingresá un número de documento válido (6 a 20 caracteres).';
  }

  get placeholderCelular(): string {
    const p = this.paisActual;
    return p ? `${p.longitud} dígitos` : 'Tu celular';
  }

  get errorCelular(): string {
    const p = this.paisActual;
    return p ? `El celular de ${p.nombre} tiene ${p.longitud} dígitos.` : 'Ingresá tu celular.';
  }

  get etiquetasPasos(): { clave: Paso; texto: string }[] {
    return [
      { clave: 'documento', texto: 'Documento' },
      { clave: 'contacto', texto: 'Datos' },
      { clave: 'codigo', texto: 'Código' },
      { clave: 'clave', texto: 'Clave' },
    ];
  }

  estaHecho(p: Paso): boolean {
    const orden: Paso[] = ['documento', 'contacto', 'codigo', 'clave', 'listo'];
    return orden.indexOf(p) < orden.indexOf(this.paso);
  }

  /** En modo reclamo el paso de datos no se recorre: se tacha en vez de desaparecer. */
  esSalteado(p: Paso): boolean {
    return p === 'contacto' && this.modo === 'reclamo';
  }

  private mensaje(e: any, porDefecto: string): string {
    const m = e?.error?.message;
    // Nest devuelve string en las excepciones propias y array cuando valida con DTOs.
    if (Array.isArray(m)) return m.join(' ');
    return m ?? porDefecto;
  }
}
